import type {
  CubeHellAction,
  CubeHellPlayerId,
  CubeHellState,
  GuessAction,
  QueryCellAction,
  QueryLayerAction,
  ValidationResult,
} from "../types";
import { CELL_COUNT, CUBE_COLORS, QUERY_AXES } from "../types";
import { inRange, isCell, isPlayerId, remainingQueries } from "../utils";

const ok: ValidationResult = { valid: true };

function fail(
  error: string,
  details?: Record<string, unknown>,
): ValidationResult {
  return { valid: false, error, details };
}

function validateActor(
  state: CubeHellState,
  playerId: string,
): ValidationResult {
  if (!isPlayerId(playerId)) return fail("Unknown player", { playerId });
  if (state.phase === "game-over") return fail("Game is already over");
  if (state.currentPlayerId !== playerId) {
    return fail("It is not this player's turn", {
      playerId,
      currentPlayerId: state.currentPlayerId,
    });
  }
  return ok;
}

function validateBudget(
  state: CubeHellState,
  playerId: CubeHellPlayerId,
): ValidationResult {
  const remaining = remainingQueries(state, playerId);
  if (remaining !== null && remaining <= 0) {
    return fail("No queries remaining", {
      playerId,
      maxQueriesPerPlayer: state.maxQueriesPerPlayer,
    });
  }
  return ok;
}

export function validateQueryCell(
  state: CubeHellState,
  action: QueryCellAction,
): ValidationResult {
  const actor = validateActor(state, action.playerId);
  if (!actor.valid) return actor;
  const budget = validateBudget(state, action.playerId);
  if (!budget.valid) return budget;
  if (!inRange(action.x) || !inRange(action.y) || !inRange(action.z)) {
    return fail("Cell coordinates out of range", {
      x: action.x,
      y: action.y,
      z: action.z,
    });
  }
  return ok;
}

export function validateQueryLayer(
  state: CubeHellState,
  action: QueryLayerAction,
): ValidationResult {
  const actor = validateActor(state, action.playerId);
  if (!actor.valid) return actor;
  const budget = validateBudget(state, action.playerId);
  if (!budget.valid) return budget;
  if (!QUERY_AXES.includes(action.axis)) {
    return fail("Unknown query axis", { axis: action.axis });
  }
  if (!inRange(action.index)) {
    return fail("Layer index out of range", { index: action.index });
  }
  if (!CUBE_COLORS.includes(action.color)) {
    return fail("Unknown cube colour", { color: action.color });
  }
  return ok;
}

export function validateGuess(
  state: CubeHellState,
  action: GuessAction,
): ValidationResult {
  // By design a guess may be any 27-cell array — even a structurally-impossible
  // one (it simply won't match). We check shape and cell values only, not
  // arrangement legality (counts/connectivity).
  const actor = validateActor(state, action.playerId);
  if (!actor.valid) return actor;
  if (action.arrangement.length !== CELL_COUNT) {
    return fail("Guess must declare all 27 cells", {
      length: action.arrangement.length,
    });
  }
  for (let i = 0; i < action.arrangement.length; i += 1) {
    if (!isCell(action.arrangement[i])) {
      return fail("Guess contains an invalid cell value", {
        i,
        cell: action.arrangement[i],
      });
    }
  }
  return ok;
}

export function validateAction(
  state: CubeHellState,
  action: CubeHellAction,
): ValidationResult {
  switch (action.type) {
    case "QueryCell":
      return validateQueryCell(state, action);
    case "QueryLayer":
      return validateQueryLayer(state, action);
    case "Guess":
      return validateGuess(state, action);
  }
}
